import styled from "@emotion/styled"
import type { IconName } from "@porsche-design-system/components-react"
import { PButtonPure, PHeading, PIcon, PText } from "@porsche-design-system/components-react"
import {
	breakpoint,
	colorSurface,
	radiusXl,
	spacingStaticLg,
	spacingStaticMd,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import type { ActionItem as ActionItemType, ListingScoreHintsView } from "@slfinrtl/ui-bff-routes"
import { useCallback } from "react"
import { FormattedMessage } from "react-intl"
import { useDealerAppRoutes } from "src/common/routes/useDealerAppRoutes"
import { ActionItem, ListingScoreIcon } from "src/components"
import type { VehicleDetailsStage } from "src/screens/vehicleDetails/common/VehicleDetailsStage"
import { useVehicleDetailsContext } from "../../../context/VehicleDetailsContext"
import { listingScoreHintsMessages as messages } from "./listingScoreHints.messages"

interface ScoreHintsProps {
	conditionAndServiceEditView?: ActionItemType
	imageEditView?: ActionItemType
	isExpanded: boolean
	onEditClick: (editStage: VehicleDetailsStage) => void
	onExpandedChange: (isExpanded: boolean) => void
	porscheApprovedEditView?: ActionItemType
	view: ListingScoreHintsView
	visibilityEditView?: ActionItemType
}

export const ListingScoreHints = ({
	conditionAndServiceEditView,
	imageEditView,
	isExpanded,
	onEditClick,
	onExpandedChange,
	porscheApprovedEditView,
	view,
	visibilityEditView,
}: ScoreHintsProps) => {
	const { inventoryId } = useVehicleDetailsContext()
	const routes = useDealerAppRoutes()
	const imageEditRoute = routes?.inventoryList?.vehicleDetails?.mediaEdit?.url?.(inventoryId)

	const toggleIsExpanded = () => onExpandedChange(!isExpanded)

	const handleConditionAndServiceEditClick = useCallback(() => onEditClick("conditionService"), [onEditClick])
	const handleVisibilityEditClick = useCallback(() => onEditClick("visibility"), [onEditClick])
	const handlePorscheApprovedEditClick = useCallback(() => onEditClick("porscheApproved"), [onEditClick])

	const scoreHints = view.scoreHints ?? []

	return (
		<details open={isExpanded}>
			<StyledSummary onClick={(event) => event.preventDefault()} tabIndex={-1}>
				<HeaderContainer>
					<HeadingIconWrapper>
						<PHeading size="lg" tag="h2">
							<FormattedMessage {...messages.headline} />
						</PHeading>
						<ListingScoreIcon color={view.colorIndicator} />
					</HeadingIconWrapper>

					<PText size="lg">
						<FormattedMessage
							{...messages.headlineSuggestionsAddition}
							values={{ completed: view.completedCount, total: view.totalRequirementsCount }}
						/>
					</PText>
				</HeaderContainer>

				{scoreHints.length === 0 && (
					<Description color="contrast-medium">
						<FormattedMessage {...messages.descriptionNoSuggestions} />
					</Description>
				)}

				{scoreHints.length > 0 && (
					<>
						<Description color="contrast-medium">
							<FormattedMessage {...messages.description} />
						</Description>
						<PButtonPure icon="none" underline onClick={toggleIsExpanded}>
							<FormattedMessage
								{...(isExpanded
									? messages.expansionToggleButtonLabelExpanded
									: messages.expansionToggleButtonLabelCollapsed)}
							/>
						</PButtonPure>
					</>
				)}
			</StyledSummary>

			{scoreHints.length > 0 && (
				<HintsContainer>
					{scoreHints.map(({ description, hintId, icon, targetLink, title }) => (
						<Hint key={hintId}>
							<TitleWrapper>
								<PIcon name={icon as IconName} />
								<PText weight="semibold" size="sm">
									{title}
								</PText>
							</TitleWrapper>
							<HintDescription color="contrast-medium">{description}</HintDescription>

							{targetLink === "vehicleMediaEdit" && imageEditView && (
								<StyledActionItem route={imageEditRoute} view={imageEditView} icon="edit" />
							)}
							{targetLink === "conditionAndServiceEdit" && conditionAndServiceEditView && (
								<StyledActionItem
									onClick={handleConditionAndServiceEditClick}
									view={conditionAndServiceEditView}
									icon="edit"
								/>
							)}
							{targetLink === "visibility" && visibilityEditView && (
								<StyledActionItem
									onClick={handleVisibilityEditClick}
									view={visibilityEditView}
									icon="edit"
								/>
							)}
							{targetLink === "porscheApproved" && porscheApprovedEditView && (
								<StyledActionItem
									onClick={handlePorscheApprovedEditClick}
									view={porscheApprovedEditView}
									icon="edit"
								/>
							)}
						</Hint>
					))}
				</HintsContainer>
			)}
		</details>
	)
}

const StyledSummary = styled.summary`
	display: flex;
	flex-direction: column;
	align-items: start;
	gap: ${spacingStaticMd};
	list-style: none;
	
	&::-webkit-details-marker {
		display: none; /* remove triangle in safari */
	}
`

const HeaderContainer = styled.div`
	display: flex;
	flex-direction: column;

	@media (min-width: ${breakpoint.xs}px) {
		flex-direction: row;
		gap: ${spacingStaticSm};
	}
`

const HeadingIconWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingStaticSm};
	flex-direction: row;
`

const Description = styled(PText)`
	@media (min-width: ${breakpoint.m}px) {
		max-width: 60vw;
	}
`

const HintsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticMd};
	margin-top: ${spacingStaticLg};
`

const Hint = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	gap: ${spacingStaticSm};
	padding: ${spacingStaticMd};
	background: ${colorSurface};
	border-radius: ${radiusXl};
	
	@media (min-width: ${breakpoint.m}px) {
		grid-template-columns: minmax(0, 60vw) auto;
	}
`

const TitleWrapper = styled.div`
	display: flex;
	gap: ${spacingStaticSm};
`

const HintDescription = styled(PText)`
	grid-column: 1 / -1;

	@media (min-width: ${breakpoint.m}px) {
		grid-column: 1;
	}
`

const StyledActionItem = styled(ActionItem)`
	white-space: nowrap;
	grid-column: 2;
	grid-row: 1;
	justify-self: end;
`
